import { APICountInstancesResponse, DirectedTriple } from '@ardoq/api-types';
import {
  isArdoqError,
  LOADING,
  Result,
  type LoadState,
} from '@ardoq/common-helpers';
import { MetaModelViewGraphNode } from '@ardoq/graph';
import { logError } from '@ardoq/logging';
import { last } from 'lodash';
import { InstanceCounts, TraversalBuilderState, TripleOptions } from '../types';
import {
  getHasTripleOptions,
  tripleOptionToFilteredAndSortedTripleOptions,
} from './filterOptions';
import { pathToKey } from './pathToKey';
import { getComponentTypeDisplayLabel } from './getComponentTypeDisplayLabel';
import { hasGraphIds } from 'traversals/pathAndGraphUtils';
import { isHierarchyCollapsedByRules } from './pathCollapsing';

const getZeroedInstanceCounts = (
  instanceCounts: Map<string, InstanceCounts>
): Map<string, InstanceCounts> => {
  const resetInstanceCounts = Array.from(
    instanceCounts,
    ([key, instanceCount]) =>
      [key, { ...instanceCount, counts: 0 }] satisfies [string, InstanceCounts]
  );
  return new Map(resetInstanceCounts);
};

const updateInstanceCounts = (
  newInstanceCounts: Map<string, InstanceCounts>,
  currentInstanceCounts: Map<string, InstanceCounts>,
  graphNodeId: string,
  counts?: number
) => {
  newInstanceCounts.set(graphNodeId, {
    ...currentInstanceCounts.get(graphNodeId)!,
    counts,
  });
};

const updateLabelWithCounts = (
  graphNode: MetaModelViewGraphNode,
  counts: number | undefined,
  isHierarchyWildcardActive: boolean
) => {
  // Use the friendly display label (anyType -> "Context component type" /
  // "Any Type") so the wildcard start node keeps its label when counts arrive;
  // the raw `metaData.label` sentinel is left untouched as it still serialises
  // to the backend. Concrete types map to themselves.
  const displayLabel = getComponentTypeDisplayLabel(
    graphNode.metaData!.label,
    isHierarchyWildcardActive
  );
  graphNode.setMetaData({
    ...graphNode.metaData!,
    // Mirror createStartNode's no-count handling: with a count, append it; with
    // no count, fall back to `undefined` for concrete types (so
    // `getLabelWithCount` shows the raw `metaData.label`, which is the real
    // type name) but surface the friendly `displayLabel` for the wildcard node,
    // otherwise the fallback would re-expose the `anyType` sentinel in the UI.
    labelWithCount:
      typeof counts === 'number'
        ? `${displayLabel} (${counts})`
        : displayLabel === graphNode.metaData!.label
          ? undefined
          : displayLabel,
  });
};

/**
 * `expandedStartSetCount` is the size of the (possibly expanded) start set: the
 * expanded hierarchy when start-set expansion is active, the plain start set
 * otherwise. Once a recursive collapsing rule stands in for that hierarchy the
 * node represents the roots the expansion started from again, which is what
 * `startSetCount` reports.
 */
const getCountForStartNode = (
  state: TraversalBuilderState,
  {
    startSetCount,
    expandedStartSetCount,
  }: TraversalBuilderState['startSetCounts'] & object
) =>
  isHierarchyCollapsedByRules(state.pathCollapsing.rules)
    ? startSetCount
    : expandedStartSetCount;

/**
 * Re-labels the start node after the collapsing rules changed, so the count
 * follows the hierarchy appearing and disappearing. Needs the counts the last
 * response reported; without them the label is left as it is.
 */
export const updateStartNodeCountForCollapsedHierarchy = (
  state: TraversalBuilderState
): TraversalBuilderState => {
  const { startNode, startSetCounts } = state;
  if (!startNode || !startSetCounts) {
    return state;
  }
  const startNodeCount = getStartNodeCount(
    getCountForStartNode(state, startSetCounts)
  );
  const instanceCounts = new Map(state.instanceCounts);
  const currentCounts = instanceCounts.get(startNode.id);
  if (currentCounts) {
    instanceCounts.set(startNode.id, {
      ...currentCounts,
      counts: startNodeCount,
    });
  }
  updateLabelWithCounts(
    startNode,
    startNodeCount,
    Boolean(state.startSetExpansionState)
  );
  return {
    ...state,
    graphNodes: new Map(state.graphNodes),
    instanceCounts,
  };
};

export const getStartNodeCount = (
  expandedStartSetCount: number
): number | undefined =>
  // `expandedStartSetCount` is the size of the (possibly expanded) start set:
  // the expanded hierarchy when start-set expansion is active, and the plain
  // start set otherwise. It is therefore always the right count for the start
  // node, including when there are no traversal paths yet. A negative value is
  // the reset sentinel and clears the label.
  expandedStartSetCount >= 0 ? expandedStartSetCount : undefined;

export const buildPathKeyToGraphNodeIdIndex = (
  instanceCounts: Map<string, InstanceCounts>
): Map<string, string> =>
  new Map(
    Array.from(instanceCounts.values()).map(({ pathKey, graphNodeId }) => [
      pathKey,
      graphNodeId,
    ])
  );

/**
 * A collapsing rule's virtual edge carries the real terminal node's graph id
 * (`traversals/pathAndGraphUtils.ts`), so this resolves a graph node id for a
 * hop whether or not it's part of a collapsed hierarchy chain.
 */
export const resolveGraphNodeIdFromTriple = (
  triple: DirectedTriple
): string | null => {
  if (!hasGraphIds(triple)) return null;
  const { direction, sourceGraphId, targetGraphId } = triple;
  return direction === 'outgoing' ? targetGraphId : sourceGraphId;
};

export const getComponentCount = (
  graphNodeId: string | null,
  instanceCounts: Map<string, InstanceCounts>
): LoadState<number> => {
  if (!graphNodeId) return LOADING;
  return instanceCounts.get(graphNodeId)?.counts ?? LOADING;
};

export const setInstanceCounts = (
  state: TraversalBuilderState,
  result: Result<APICountInstancesResponse>
) => {
  if (isArdoqError(result)) {
    return state;
  }

  const { traversalCounts, startSetCount, expandedStartSetCount } = result;
  const startSetCounts = { startSetCount, expandedStartSetCount };
  const updatedGraphNodes = new Map(state.graphNodes);
  const updatedInstanceCounts = getZeroedInstanceCounts(state.instanceCounts);
  const pathKeyToGraphNodeId = buildPathKeyToGraphNodeIdIndex(
    state.instanceCounts
  );

  const { startNode } = state;
  if (!startNode) {
    return state;
  }
  const isHierarchyWildcardActive = Boolean(state.startSetExpansionState);

  // The start node represents the (possibly expanded) start set, so its count
  // comes from the start-set counts rather than from the per-path counts.
  const startNodeCount = getStartNodeCount(
    getCountForStartNode(state, startSetCounts)
  );

  updateInstanceCounts(
    updatedInstanceCounts,
    state.instanceCounts,
    startNode.id,
    startNodeCount
  );
  updateLabelWithCounts(startNode, startNodeCount, isHierarchyWildcardActive);

  traversalCounts.forEach(({ path, counts: countsList }) => {
    const currentPath: DirectedTriple[] = [];
    path.forEach((directedTriple, index) => {
      currentPath.push(directedTriple);
      // The first triple represents basically two counts, the start node
      // and the first traversal step. The start set count doesn't need to be
      // updated, the count for that is the size of the start set.
      const counts = countsList[index + 1];
      const graphNodeId = pathKeyToGraphNodeId.get(pathToKey(currentPath));
      const graphNode = graphNodeId ? updatedGraphNodes.get(graphNodeId) : null;
      if (!(graphNodeId && graphNode)) {
        logError(Error('Missing graph node in setInstanceCounts'), null, {
          graphNodeId,
        });
        return;
      }

      const prevCount = updatedInstanceCounts.get(graphNodeId)?.counts ?? 0;
      if (counts < prevCount) {
        // For strict matching, the returned counts for the various nodes in
        // a path can vary depending on the path. We will never know the exact
        // count for a node – showing the biggest number is the best we can do.
        // So, we skip any updates if the count is less than or equal to the
        // previous.
        return;
      }
      updateInstanceCounts(
        updatedInstanceCounts,
        state.instanceCounts,
        graphNodeId,
        counts
      );
      updateLabelWithCounts(graphNode, counts, isHierarchyWildcardActive);
    });
  });

  return {
    ...state,
    startSetCounts,
    graphNodes: updatedGraphNodes,
    instanceCounts: updatedInstanceCounts,
  };
};

export const setOptionsInstanceCounts = (
  state: TraversalBuilderState,
  // TODO (chriskr): Use the Result type when it's ready.
  instanceCountsResponse: Result<APICountInstancesResponse> | null
): TraversalBuilderState => {
  if (instanceCountsResponse === null) {
    return state;
  }

  if (isArdoqError(instanceCountsResponse)) {
    return {
      ...state,
      tripleOptions: {
        ...state.tripleOptions,
        optionCountLoadingState: 'loaded',
      },
      filteredTripleOptions: {
        ...state.filteredTripleOptions,
        optionCountLoadingState: 'loaded',
      },
    };
  }

  const { traversalCounts } = instanceCountsResponse;

  const counts = new Map(
    traversalCounts.map(({ path, counts: currentCounts }) => [
      pathToKey(path),
      last(currentCounts),
    ])
  );
  const { tripleOptions } = state;

  const newTripleOptions: TripleOptions = {
    ...tripleOptions,
    optionCountLoadingState: 'loaded',
    instanceCountsResponse,
    incoming: tripleOptions.incoming.map(option => ({
      ...option,
      instanceCounts: counts.get(option.countKey) ?? null,
    })),
    outgoing: tripleOptions.outgoing.map(option => ({
      ...option,
      instanceCounts: counts.get(option.countKey) ?? null,
    })),
  };

  const filteredTripleOptions = tripleOptionToFilteredAndSortedTripleOptions(
    newTripleOptions,
    state.triplesSortAndFiltersState,
    state.locale
  );

  return {
    ...state,
    tripleOptions: newTripleOptions,
    filteredTripleOptions,
    hasTripleOptions: getHasTripleOptions(
      newTripleOptions,
      state.triplesSortAndFiltersState
    ),
  };
};

export const invalidateInstanceCounts = (
  state: TraversalBuilderState
): TraversalBuilderState => ({
  ...state,
  instanceCountsVersion: state.instanceCountsVersion + 1,
});

export const setOptionCountLoadingState = (
  state: TraversalBuilderState,
  optionCountLoadingState: TripleOptions['optionCountLoadingState']
): TraversalBuilderState => {
  return {
    ...state,
    tripleOptions: {
      ...state.tripleOptions,
      optionCountLoadingState,
    },
    filteredTripleOptions: {
      ...state.filteredTripleOptions,
      optionCountLoadingState,
    },
  };
};

export const toggleShowOnlyOptionsWithInstanceCounts = (
  state: TraversalBuilderState
) => {
  const newState: TraversalBuilderState = {
    ...state,
    triplesSortAndFiltersState: {
      ...state.triplesSortAndFiltersState,
      showOnlyOptionsWithInstanceCounts:
        !state.triplesSortAndFiltersState.showOnlyOptionsWithInstanceCounts,
    },
  };

  const filteredTripleOptions = tripleOptionToFilteredAndSortedTripleOptions(
    newState.tripleOptions,
    newState.triplesSortAndFiltersState,
    newState.locale
  );
  return {
    ...newState,
    filteredTripleOptions,
    hasTripleOptions: getHasTripleOptions(
      newState.tripleOptions,
      newState.triplesSortAndFiltersState
    ),
  };
};
